import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import QRious from 'qrious'
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline'

// Components
import { Button } from '../Button'
import { Modal } from '../Modal'
import { QRIcon } from '../QRIcon'

// Utils
import { toast } from '../../utils/helpers'

/**
 *
 * LicenseQRModal
 *
 * Displays the magic link for an assigned license as a scannable QR code, with a
 * copy-to-clipboard fallback. The link opens the Fetch app landing page with the
 * assignee's email and license pre-filled.
 */
const LicenseQRModal = ({ license, setShowModal, showModal }) => {
  const magicLink = useMemo(() => {
    const params = new URLSearchParams({
      email: license.assignee.user.email,
      licenseId: license.licenseId,
    })
    return `${window.location.origin}/app/fetch?${params.toString()}`
  }, [license])

  const qrDataUrl = useMemo(
    () => new QRious({ value: magicLink, size: 400 }).toDataURL(),
    [magicLink],
  )

  return (
    <Modal
      actions={[{ type: 'cancel', label: 'Close', onClick: () => setShowModal(false) }]}
      icon={<QRIcon className="h-5 fill-white sm:h-6" />}
      content={
        <div className="mt-3 flex flex-col items-center gap-4 sm:mt-5">
          <span className="text-center text-sm text-gray-600">
            Have {license.assignee.user.firstName} {license.assignee.user.lastName} scan this QR
            code with their phone camera to log in to license{' '}
            <span className="font-bold">{license.licenseId}</span>.
          </span>

          <img
            alt={`Login QR code for license ${license.licenseId}`}
            className="h-64 w-64"
            src={qrDataUrl}
          />

          <Button
            background="bg-white"
            icon={<DocumentDuplicateIcon className="h-4 stroke-gray sm:h-5" />}
            label="Copy Link"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(magicLink)
                toast('Login link copied to clipboard!', 'success')
              } catch {
                toast('Failed to copy link to clipboard', 'error')
              }
            }}
            outlined
          />
        </div>
      }
      open={showModal}
      setOpen={setShowModal}
      title="License Login QR Code"
    />
  )
}

LicenseQRModal.propTypes = {
  license: PropTypes.object.isRequired,
  setShowModal: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
}

export default LicenseQRModal
